import { UserInputError } from "./errors-CEDWDXew.js";

//#region src/internal/url-helpers.ts
/**
* Parses a Sentry URL and extracts resource type and identifiers.
*
* Supported URL patterns:
* - Issue: `/issues/{issueId}` or `/organizations/{org}/issues/{issueId}`
* - Event: `/issues/{issueId}/events/{eventId}`
* - Trace: `/explore/traces/trace/{traceId}` or `/performance/trace/{traceId}`
* - Profile: `/explore/profiling/profile/{project}/{profileId}/flamegraph/`
* - Continuous profile: `/explore/profiling/profile/{project}/flamegraph/` with query params
* - AI conversation: `/explore/conversations/{conversationId}/`
* - Replay: `/explore/replays/{replayId}/` or `/replays/{replayId}/`
* - Monitor: `/crons/{monitorSlug}/` or `/monitors/{monitorSlug}/`
* - Release: `/releases/{version}/`
*
* Organization slug is extracted from:
* 1. Subdomain (e.g., `my-org.sentry.io`)
* 2. Path (e.g., `/organizations/my-org/...` or `/my-org/issues/...`)
*
* @param url - A Sentry URL to parse
* @returns Parsed URL with resource type and identifiers
* @throws UserInputError if the URL is invalid or cannot be parsed
*
* @example
* // Issue URL
* parseSentryUrl("https://my-org.sentry.io/issues/PROJECT-123")
* // { type: "issue", organizationSlug: "my-org", issueId: "PROJECT-123" }
*
* @example
* // Replay URL
* parseSentryUrl("https://my-org.sentry.io/explore/replays/abc123def456/")
* // { type: "replay", organizationSlug: "my-org", replayId: "abc123def456" }
*
* @example
* // Monitor URL
* parseSentryUrl("https://my-org.sentry.io/crons/my-cron-job/")
* // { type: "monitor", organizationSlug: "my-org", monitorSlug: "my-cron-job" }
*/
function parseSentryUrl(url) {
	if (!url || typeof url !== "string") throw new UserInputError("Invalid Sentry URL. URL must be a non-empty string.");
	if (!url.startsWith("http://") && !url.startsWith("https://")) throw new UserInputError("Invalid Sentry URL. Must start with http:// or https://");
	let parsedUrl;
	try {
		parsedUrl = new URL(url);
	} catch {
		throw new UserInputError(`Invalid Sentry URL. Unable to parse URL: ${url}`);
	}
	const pathParts = parsedUrl.pathname.split("/").filter(Boolean);
	const organizationSlug = extractOrganizationSlug(parsedUrl, pathParts);
	return identifyResource(parsedUrl, pathParts, organizationSlug);
}
/**
* Extracts organization slug from URL.
*
* Checks in order:
* 1. `/organizations/{org}/...` path format
* 2. `/{org}/issues/...` path format (org before known segments)
* 3. Subdomain (e.g., `my-org.sentry.io`) - excluding region prefixes
*/
function extractOrganizationSlug(parsedUrl, pathParts) {
	if (pathParts.includes("organizations")) {
		const orgIndex = pathParts.indexOf("organizations");
		const slug = pathParts[orgIndex + 1];
		if (slug) return slug;
	}
	const knownSegments = [
		"issues",
		"explore",
		"performance",
		"projects",
		"settings",
		"replays",
		"releases",
		"crons",
		"monitors",
		"alerts",
		"feedback",
		"dashboards",
		"discover",
		"insights",
		"preprod"
	];
	if (pathParts.length > 1 && !knownSegments.includes(pathParts[0]) && knownSegments.includes(pathParts[1])) return pathParts[0];
	const hostParts = parsedUrl.hostname.split(".");
	const regionPrefixes = [
		"us",
		"eu",
		"de",
		"www"
	];
	if (hostParts.length >= 2 && !regionPrefixes.includes(hostParts[0])) return hostParts[0];
	throw new UserInputError("Invalid Sentry URL. Could not determine organization from URL.");
}
function escapeRegex(value) {
	return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}
function extractPathGroup(pathParts, patterns, groupName) {
	const path = pathParts.join("/");
	for (const pattern of patterns) {
		const match = pattern.exec(path);
		const value = match?.groups?.[groupName];
		if (value) return value;
	}
	return void 0;
}
/**
* Identifies the resource type and extracts relevant identifiers.
*/
function identifyResource(parsedUrl, pathParts, organizationSlug) {
	const profilingIndex = pathParts.indexOf("profiling");
	if (profilingIndex !== -1 && pathParts[profilingIndex + 1] === "profile") {
		const projectSlug = pathParts[profilingIndex + 2];
		const nextPart = pathParts[profilingIndex + 3];
		const profileId = nextPart && nextPart !== "flamegraph" ? nextPart : void 0;
		const profilerId = parsedUrl.searchParams.get("profilerId") || void 0;
		const start = parsedUrl.searchParams.get("start") || void 0;
		const end = parsedUrl.searchParams.get("end") || void 0;
		return {
			type: "profile",
			organizationSlug,
			projectSlugOrId: projectSlug,
			profileId,
			profilerId,
			start,
			end
		};
	}
	const conversationId = extractPathGroup(pathParts, [
		/^explore\/conversations\/(?<conversationId>[^/]+)$/,
		/^organizations\/[^/]+\/explore\/conversations\/(?<conversationId>[^/]+)$/,
		/* @__PURE__ */ new RegExp(`^${escapeRegex(organizationSlug)}\\/explore\\/conversations\\/(?<conversationId>[^/]+)$`)
	], "conversationId");
	if (conversationId) {
		const project = parsedUrl.searchParams.get("project") || void 0;
		const spanId = parsedUrl.searchParams.get("spanId") || void 0;
		const start = parsedUrl.searchParams.get("start") || void 0;
		const end = parsedUrl.searchParams.get("end") || void 0;
		return {
			type: "ai_conversation",
			organizationSlug,
			conversationId: decodeURIComponent(conversationId),
			projectSlugOrId: project,
			spanId,
			start,
			end
		};
	}
	const replaysIndex = pathParts.indexOf("replays");
	if (replaysIndex !== -1) {
		const replayId = pathParts[replaysIndex + 1];
		if (replayId && replayId !== "selectors") return {
			type: "replay",
			organizationSlug,
			replayId
		};
	}
	const cronsIndex = pathParts.indexOf("crons");
	const monitorsIndex = pathParts.indexOf("monitors");
	const monitorPathIndex = cronsIndex !== -1 ? cronsIndex : monitorsIndex;
	if (monitorPathIndex !== -1) {
		const nextPart = pathParts[monitorPathIndex + 1];
		if (nextPart && nextPart !== "new") {
			const afterNext = pathParts[monitorPathIndex + 2];
			if (afterNext && afterNext !== "details") return {
				type: "monitor",
				organizationSlug,
				projectSlugOrId: nextPart,
				monitorSlug: afterNext
			};
			return {
				type: "monitor",
				organizationSlug,
				monitorSlug: nextPart
			};
		}
	}
	const releasesIndex = pathParts.indexOf("releases");
	if (releasesIndex !== -1) {
		const releaseVersion = pathParts[releasesIndex + 1];
		if (releaseVersion && releaseVersion !== "new-events" && releaseVersion !== "all-events") return {
			type: "release",
			organizationSlug,
			releaseVersion
		};
	}
	const traceIndex = pathParts.indexOf("trace");
	if (traceIndex !== -1) {
		const traceId = pathParts[traceIndex + 1];
		if (traceId) {
			const nodeParam = parsedUrl.searchParams.get("node");
			let spanId;
			if (nodeParam?.startsWith("span-")) spanId = nodeParam.slice(5);
			return {
				type: "trace",
				organizationSlug,
				traceId,
				spanId
			};
		}
	}
	if (pathParts.includes("performance") && pathParts.includes("summary")) {
		const transaction = parsedUrl.searchParams.get("transaction") || void 0;
		if (transaction) return {
			type: "unknown",
			organizationSlug,
			transaction
		};
	}
	const issuesIndex = pathParts.indexOf("issues");
	if (issuesIndex !== -1) {
		const issueId = pathParts[issuesIndex + 1];
		if (issueId) {
			const eventsIndex = pathParts.indexOf("events", issuesIndex);
			if (eventsIndex !== -1) {
				const eventId = pathParts[eventsIndex + 1];
				if (eventId) return {
					type: "event",
					organizationSlug,
					issueId,
					eventId
				};
			}
			return {
				type: "issue",
				organizationSlug,
				issueId
			};
		}
	}
	const preprodIndex = pathParts.indexOf("preprod");
	if (preprodIndex !== -1 && pathParts[preprodIndex + 1] === "snapshots") {
		const snapshotId = pathParts[preprodIndex + 2];
		if (snapshotId) {
			const selectedSnapshot = parsedUrl.searchParams.get("selectedSnapshot") || void 0;
			return {
				type: "snapshot",
				organizationSlug,
				snapshotId,
				selectedSnapshot
			};
		}
	}
	return {
		type: "unknown",
		organizationSlug
	};
}
/**
* Checks if a URL appears to be a Sentry profile URL.
*
* @param url - URL to check
* @returns True if the URL looks like a profile URL
*/
function isProfileUrl(url) {
	try {
		const parsed = parseSentryUrl(url);
		return parsed.type === "profile";
	} catch {
		return false;
	}
}

//#endregion
export { isProfileUrl, parseSentryUrl };
//# sourceMappingURL=url-helpers-CcAcmPuu.js.map